/**
 * ByoCreateCanvas — BYO flow create experience.
 *
 * Uses SourcePipelineContainer (the real FlowNodes DAG) throughout the entire
 * create flow. Before a source is configured, SourcePipeline injects a virtual
 * SOURCE shell node (dashed, like CODE/DESTINATION) via byoCreate.onSourceClick.
 * Once Step 1 creates the source, the page replaces /custom/new with the new
 * flow's own canvas route (/integrate/flows/:flowNodeId/edit), which renders
 * the same DAG with the Code + Add-Destination shells. This used to update
 * in place on /custom/new, but then the URL never identified the flow, so a
 * reload/back lost the half-built flow and later header-name edits were
 * never saved. NEX-22636.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Prompt, Redirect, useHistory } from 'react-router-dom';
import { Button } from '@mui/material';

import { ROUTE_CONNECT, ROUTE_FLOWS } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import SourcePipelineContainer from 'js/components/pipelines/SourcePipelineContainer';
import RouteLoadingComponent from 'js/components/route_utils/RouteLoadingComponent';
import { useGrowthBookContext } from 'new/contexts/GrowthBookProvider';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import type { ScreenConfig } from 'new/routes/RouterManager';

import { StudioDraftProvider, useStudioDraftCtx } from '../studio/StudioDraftContext';
import { ByoSourceSetupForm } from './ByoSourceSetupForm';

import classes from './ByoCreateCanvas.module.scss';

// Same message FormProvider's route <Prompt> uses for the app's other editors.
const DISCARD_MESSAGE =
  'Are you sure you want to exit this editor? All of your current changes will be discarded.';

// ── Canvas inner component ────────────────────────────────────────────────────

function CanvasRoot() {
  const history = useHistory();
  const { draft, sourceCreateProgressRef } = useStudioDraftCtx();
  const { refreshPipelineById } = usePipelinesAdapter();

  const [showSourceForm, setShowSourceForm] = useState(false);

  const sourceId = draft.sourceId as number | null;

  const sourceEntity = useSelector((state: { entities?: { sources?: Record<number, any> } }) =>
    sourceId ? state.entities?.sources?.[sourceId] : null,
  );

  // SourcePipelineContainer reads dataSource.origin_node_id to find the flow.
  // Before source is created pass a stub so the container doesn't crash.
  const dataSource = useMemo(() => sourceEntity ?? { origin_node_id: null }, [sourceEntity]);

  const onClickSourceShell = useCallback(() => setShowSourceForm(true), []);

  // byoCreate is only consumed by SourcePipeline when flow is null (pre-source).
  // After source creation the real flow is in Redux and this prop is ignored.
  const byoCreate = useMemo(() => ({ onSourceClick: onClickSourceShell }), [onClickSourceShell]);

  // Before Step 1 creates the source, the typed flow name is the only thing
  // on this page — guard it with the router's <Prompt> (the app-wide guard
  // FormProvider uses; getUserConfirmation renders the standard dialog), so
  // Discard / back / in-app navigation confirm instead of silently dropping
  // it. Suppressed while the source form overlay is open: that Form mounts
  // its own <Prompt>, and history supports only one active block.
  // A source Step 1 created before a later call failed also counts: it exists
  // server-side but only this draft knows about it.
  const hasUnsavedDraft =
    !sourceId && (!!draft.name.trim() || sourceCreateProgressRef.current.sourceId != null);

  return (
    <>
      <Prompt when={hasUnsavedDraft && !showSourceForm} message={DISCARD_MESSAGE} />
      <div className={classes.root} data-testid="byo-canvas">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className={classes.header} data-testid="byo-canvas-header">
          <div className={classes.headerBreadcrumb}>
            <span>Flows</span>
            <span className={classes.headerSep}>/</span>
            <span className={classes.headerSub}>New BYO Flow</span>
          </div>
          <div className={classes.headerNameWrap}>
            <input
              className={classes.headerName}
              value={draft.name}
              onChange={(e) => draft.setName(e.target.value)}
              // The name is persisted only when Step 1 creates the source; an
              // edit after that was silently dropped. Normally the page has
              // already moved to the flow's own canvas by then (see onDismiss
              // below) — this covers the no-flowNodeId fallback.
              readOnly={!!sourceId}
              placeholder="Flow name"
              aria-label="Flow name"
            />
          </div>
          <div className={classes.headerActions}>
            <Button variant="outlined" size="small" onClick={() => history.replace(ROUTE_FLOWS)}>
              Discard
            </Button>
          </div>
        </div>

        {/* ── Canvas — real FlowNodes DAG, SOURCE shell before source is set ── */}
        <div className={classes.canvas} data-testid="byo-canvas-body">
          <SourcePipelineContainer
            dataSource={dataSource}
            refreshPipeline={refreshPipelineById}
            isPreview={false}
            fullScreen
            byoCreate={byoCreate}
          />
        </div>
      </div>

      {/* ── Source setup form (overlay) ─────────────────────────────────── */}
      {showSourceForm && (
        <ByoSourceSetupForm
          onDismiss={(result: any) => {
            if (result?.sourceId) {
              draft.setSourceId(result.sourceId);
              if (result.flowNodeId != null) {
                // The flow now exists server-side — move to its real canvas
                // route (the same one ByoDestShellNode and this step's own
                // no-parent fallback land on; it renders the BYO Code +
                // Add-Destination shells for a sink-less custom flow). Staying
                // on /custom/new meant reload/back lost the half-built flow.
                // NEX-22636.
                history.replace(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/${result.flowNodeId}/edit`);
                return;
              }
            }
            setShowSourceForm(false);
          }}
        />
      )}
    </>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────

export interface ByoCreateCanvasProps {
  isModal?: boolean;
  onDismiss?: (data: any) => void;
}

export function ByoCreateCanvas(_props: ByoCreateCanvasProps = {}) {
  // Direct links to /custom/new bypassed FlowSelectionPage's UI_ENABLE_BYO_V2
  // check and opened the v2 canvas for flag-off orgs. Fall back the same way
  // FlowSelectionPage does (legacy DirectFlow-based BYO wizard). Wait for
  // GrowthBook first so a flag-on org isn't bounced by the default value —
  // same pattern as Tasks.tsx.
  const { isReady: isGrowthBookReady } = useGrowthBookContext();
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');
  if (!isGrowthBookReady) return <RouteLoadingComponent />;
  if (!byoV2Enabled)
    return <Redirect to={`${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/source/new`} />;

  return (
    <StudioDraftProvider>
      <CanvasRoot />
    </StudioDraftProvider>
  );
}

ByoCreateCanvas.screenConfig = {
  domain: 'main',
  path: [`${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/new`],
  screenName: 'BYO Flow',
  pageTitle: 'BYO Flow',
  header: {
    title: 'BYO Flow',
    slugTitle: 'BYO Flow',
  },
} as ScreenConfig<any>;

export default ByoCreateCanvas;
